import { FormHelperText, Stack } from '@mui/material';
import {
  useAddressForm,
  useContactDetailsForm,
  useFamilyMembersForm,
  useIsDesktop,
  useMaritalStatusForm,
} from 'Hooks';
import { AddChild } from './AddChild';
import { Address } from './Address';
import { ContactDetails } from './ContactDetails';
import { MaritalStatus } from './MaritalStatus';
import { useContext, useState } from 'react';
import { RequestsContext } from 'Context';
import { FormProvider } from 'react-hook-form';
import { IUpdatePersonalDetails } from 'Interfaces/updatePersonalDetails';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { FormActionButtons } from 'Modules/Requests/FormActionButtons';
import { useNavigate } from 'react-router-dom';

interface ISections {
  nextStep: () => void;
}

export const Sections = ({ nextStep }: ISections) => {
  const { setRequestsData, setReqAttachSchema } = useContext(RequestsContext);
  const [submitError, setSubmitError] = useState('');

  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const contactDetailsForm = useContactDetailsForm();
  const martialStatusForm = useMaritalStatusForm();
  const {
    addressForm,
    isDifferentMailAddress,
    setIsDifferentMailAddress,
    isReset,
  } = useAddressForm();
  const familyMembersForm = useFamilyMembersForm();

  const onSubmit = async (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setSubmitError('');

    const validData: Partial<IUpdatePersonalDetails> = {};

    await Promise.all([
      contactDetailsForm.handleSubmit((data) => {
        validData.contactDetails = data;
      })(),
      martialStatusForm.handleSubmit((data) => {
        validData.maritalStatus = data;
      })(),
      addressForm.handleSubmit((data) => {
        validData.addresses = data.addresses;

        //fixed both options should have same address type
        if (validData.addresses && validData.addresses[1])
          validData.addresses[1].isMailBox = true;
      })(),
      familyMembersForm.handleSubmit((data) => {
        validData.familyMembers = data.familyMembers.filter((member) => {
          return (
            // eslint-disable-next-line eqeqeq
            member.birthDate != undefined ||
            Boolean(member.firstName && member.firstName !== '') ||
            Boolean(member.lastName && member.lastName !== '') ||
            Boolean(member.identityNumber && member.identityNumber !== '')
          );
        });
      })(),
    ]);

    if (
      !(
        contactDetailsForm.formState.isDirty ||
        contactDetailsForm.formState.dirtyFields.phonePrefix ||
        contactDetailsForm.formState.dirtyFields.extraPhonePrefix ||
        contactDetailsForm.formState.dirtyFields.phoneNumber ||
        contactDetailsForm.formState.dirtyFields.extraPhoneNumber ||
        contactDetailsForm.formState.dirtyFields.email ||
        martialStatusForm.formState.isDirty ||
        addressForm.formState.isDirty ||
        familyMembersForm.formState.isDirty
      )
    )
      setSubmitError(Dictionary[DICTIONARY.UpdatePersonalDetails].needToEdit);
    else if (
      Object.values({
        ...contactDetailsForm.formState.errors,
        ...martialStatusForm.formState.errors,
        ...addressForm.formState.errors,
        ...familyMembersForm.formState.errors,
      }).length === 0
    ) {
      setReqAttachSchema(() => ({
        maritalStatus:
          martialStatusForm.formState.isDirty &&
          validData.maritalStatus?.status !== -1
            ? validData.maritalStatus?.status
            : undefined,
        addresses:
          addressForm.formState.isDirty &&
          validData.addresses &&
          validData.addresses[0]?.zipCode !== '',
        familyMembers:
          Object.values(familyMembersForm.formState.dirtyFields).length > 0 &&
          validData.familyMembers &&
          validData.familyMembers.length > 0,
      }));

      const sanitizedRequestDataJson = {
        contactDetails:
          contactDetailsForm.formState.isDirty ||
          contactDetailsForm.formState.dirtyFields.phoneNumber ||
          contactDetailsForm.formState.dirtyFields.extraPhoneNumber ||
          contactDetailsForm.formState.dirtyFields.email
            ? {
                ...validData.contactDetails,
                phonePrefix:
                  validData.contactDetails?.phonePrefix === -1
                    ? null
                    : validData.contactDetails?.phonePrefix,
                extraPhonePrefix:
                  validData.contactDetails?.extraPhonePrefix === -1
                    ? null
                    : validData.contactDetails?.extraPhonePrefix,
              }
            : undefined,
        maritalStatus: martialStatusForm.formState.isDirty
          ? validData.maritalStatus
          : undefined,
        addresses: addressForm.formState.isDirty
          ? validData.addresses
          : undefined,
        familyMembers:
          familyMembersForm.formState.isDirty ||
          Object.values(familyMembersForm.formState.dirtyFields).length > 0
            ? validData.familyMembers
            : undefined,
      };

      setRequestsData((prev) => {
        return {
          ...prev,
          requestDataJson: sanitizedRequestDataJson,
        };
      });

      nextStep();
    }
  };

  return (
    <Stack>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
        onSubmit={onSubmit}
      >
        <FormProvider {...contactDetailsForm}>
          <ContactDetails />
        </FormProvider>
        <FormProvider {...martialStatusForm}>
          <MaritalStatus />
        </FormProvider>
        <FormProvider {...addressForm}>
          <Address
            isDifferentAddress={isDifferentMailAddress}
            setIsDifferentAddress={setIsDifferentMailAddress}
            isReset={isReset}
          />
        </FormProvider>
        <FormProvider {...familyMembersForm}>
          <AddChild />
        </FormProvider>
        <Stack marginTop={'20px'}>
          {submitError !== '' && (
            <Stack alignItems={'center'}>
              <FormHelperText
                error
                role={'alert'}
                sx={{
                  fontSize: isDesktop ? '20px !important' : '18px !important',
                  lineHeight: '24px',
                  textAlign: 'center',
                  m: '0 0 30px',
                }}
              >
                {submitError}
              </FormHelperText>
            </Stack>
          )}
          <FormActionButtons
            next={onSubmit}
            previous={() => navigate('/universalRequest')}
          />
        </Stack>
      </form>
    </Stack>
  );
};
